import { GoodsIn } from "../../../api/models";
import {
  getCore,
  getListCore,
  postCore,
  putCore,
} from "../../../api/services/axiosCore.service";

/**
 * Generates labels for a specific goods-in entry.
 * @param {string|number} id - The ID of the goods-in entry.
 * @param {Object} filters - The filters to apply to the request.
 * @param {string} data - The type of labels to generate.
 * @returns {Promise<Object>} A promise that resolves to the response containing the generated labels.
 */
export const postGoodsInLabels = (id, data, filters) => {
  return postCore(`/goods_ins/${id}/labels`, data, filters).then((res) => res);
};

/**
 * Retrieves statistical data for goods-in entries.
 * @param {Object} filters - The filters to apply to the request.
 * @returns {Promise<Object>} A promise that resolves to the response containing the statistics.
 */
export const getGoodsInStats = (filters) => {
  return getCore("/goods_ins/stats", filters).then((res) => res);
};

/**
 * Retrieves a list of goods-in entries based on the provided filters.
 * @param {Object} filters - The filters to apply to the request.
 * @returns {Promise<Object>} A promise that resolves to the response containing the goods-in entries.
 */
export const getGoodsInList = (filters) => {
  return getListCore("/goods_ins", filters).then((res) => {
    res.results = res.results.map((item) => new GoodsIn(item));
    return res;
  });
};

/**
 * Retrieves available options for goods-in entries based on filters.
 * @param {Object} filters - The filters to apply to the request.
 * @returns {Promise<Object>} A promise that resolves to the response containing the goods-in options.
 */
export const getGoodsInOptions = (filters) => {
  return getCore("/goods_in_options", filters).then((res) => {
    return res.results;
  });
};

/**
 * Creates a new goods-in entry (pickup or transfer).
 * @param {Object} data - The goods in data to create.
 * @param {Object} filters - The filters to apply to the request.
 * @returns {Promise<Object>} A promise that resolves to the response containing the created goods-in entry.
 */
export const postGoodsIn = (data, filters) => {
  const url = data.isPickup ? "/pickups" : "/transfers";

  return postCore(url, data, filters).then((res) => {
    res.results = new GoodsIn(res.results);
    return res;
  });
};

/**
 * Updates a GoodsIn entity by sending a PUT request to the appropriate endpoint.
 * @param {Object} goodsIn - The GoodsIn object containing details about the entity to update.
 * @param {Object} data - The data to update the GoodsIn entity with.
 * @param {Object} filters - Additional query parameters to include in the request.
 * @returns {Promise<Object>} A promise that resolves to the updated GoodsIn entity wrapped in a response object.
 */
export const putGoodsIn = (goodsIn, data, filters) => {
  const url = goodsIn.isPickup
    ? `/pickups/${goodsIn.id}`
    : `/transfers/${goodsIn.id}`;

  return putCore(url, data, filters).then((res) => {
    res.results = new GoodsIn(res.results);
    return res;
  });
};
